import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

import {
  refreshSession as refreshSessionRequest,
  AuthUser,
  getCurrentUser,
  login as loginRequest,
  loginWithGoogle as loginWithGoogleRequest,
  logout as logoutRequest,
  toAuthUser,
} from '../services/authService';
import { buildLoginRedirectPath } from '../routes/authRedirect';
import {
  getCsrfToken,
  clearCsrfToken,
  onSessionInvalidated,
  onSessionRefreshRequested,
} from '../services/sessionManager';
import { setAccessToken as setInMemoryAccessToken } from '../shared/auth/accessTokenStore';
import {
  clearE2EAuthToken,
  getE2EAuthUser,
  hasE2EAuthToken,
  isE2EModeEnabled,
} from '../utils/e2eAuth';
import { setSentryUser, track } from '../observability/sentry';
import {
  clearHistoryOwner,
  setHistoryOwner,
} from '../modules/communicationHistory/communicationHistory';
import {
  clearPredictionFeedbackOwner,
  setPredictionFeedbackOwner,
} from '../modules/phrasePrediction/predictionFeedback';
import {
  clearPredictionSelectionOwner,
  setPredictionSelectionOwner,
} from '../modules/phrasePrediction/selectionStore';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

type AuthState = {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
};

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setError: (message: string | null) => void;
  clearError: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string, nonce?: string) => Promise<void>;
  refresh: (showLoading?: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const PUBLIC_ROUTES = new Set([
  '/',
  '/about',
  '/docs',
  '/guides',
  '/help',
  '/links',
  '/login',
  '/register',
  '/signup',
]);

const INITIAL_AUTH_STATE: AuthState = {
  status: 'loading',
  user: null,
  error: null,
};

const clearPersistedSession = (): void => {
  localStorage.removeItem('user');
  localStorage.removeItem('authToken');
};

const toAuthErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;

    if (status === 401) {
      return 'Email ou senha invalidos.';
    }

    if (status === 429) {
      return 'Muitas tentativas. Aguarde um pouco e tente novamente.';
    }

    if (!error.response) {
      return 'Sem conexao. Verifique sua internet.';
    }

    if (typeof status === 'number' && status >= 500) {
      return 'Servico instavel. Estamos tentando reconectar.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};

const isSessionRefreshCandidate = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;
  return status === 401 || status === 403;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>(INITIAL_AUTH_STATE);
  const bootstrapRefreshInFlightRef = useRef(false);
  const setError = useCallback((message: string | null): void => {
    setAuthState((previous) => {
      const nextStatus =
        previous.status === 'loading' && !previous.user
          ? 'anonymous'
          : previous.status;

      return {
        ...previous,
        status: nextStatus,
        error: message,
      };
    });
  }, []);
  const clearError = useCallback((): void => {
    setError(null);
  }, [setError]);

  const setAuthenticated = useCallback((user: AuthUser): void => {
    setHistoryOwner(user.id);
    setPredictionFeedbackOwner(user.id);
    setPredictionSelectionOwner(user.id);
    setAuthState({
      status: 'authenticated',
      user,
      error: null,
    });
  }, []);

  const setAnonymous = useCallback((error: string | null = null): void => {
    setAuthState({
      status: 'anonymous',
      user: null,
      error,
    });
  }, []);

  const clearSession = useCallback((): void => {
    setInMemoryAccessToken(null);
    clearHistoryOwner();
    clearPredictionFeedbackOwner();
    clearPredictionSelectionOwner();
    clearPersistedSession();
    clearE2EAuthToken();
    clearCsrfToken();
    setAnonymous();
    setSentryUser(null);
  }, [setAnonymous]);

  const redirectToLoginIfNeeded = useCallback((): void => {
    const { pathname, search, hash } = window.location;
    if (!PUBLIC_ROUTES.has(pathname)) {
      navigate(buildLoginRedirectPath(pathname, search, hash), { replace: true });
    }
  }, [navigate]);

  const refresh = useCallback(
    async (showLoading = false): Promise<void> => {
      if (showLoading) {
        setAuthState((previous) => ({
          ...previous,
          status: 'loading',
          error: null,
        }));
      }

      try {
        if (isE2EModeEnabled()) {
          if (hasE2EAuthToken()) {
            setInMemoryAccessToken('e2e');
            setAuthenticated(getE2EAuthUser());
            return;
          }

          setInMemoryAccessToken(null);
          setAnonymous();
          return;
        }

        const currentUser = await getCurrentUser();
        setAuthenticated(currentUser);
      } catch (error) {
        if (isSessionRefreshCandidate(error)) {
          try {
            const refreshedSession = await refreshSessionRequest();
            setInMemoryAccessToken(refreshedSession.accessToken);
            const currentUser = await getCurrentUser();
            setAuthenticated(currentUser);
            return;
          } catch {
            clearSession();
            return;
          }
        }

        clearSession();
      }
    },
    [clearSession, setAnonymous, setAuthenticated],
  );

  useEffect(() => {
    let isCancelled = false;

    const bootstrapAuth = async (): Promise<void> => {
      if (isE2EModeEnabled()) {
        await refresh(true);
        return;
      }

      if (bootstrapRefreshInFlightRef.current) {
        return;
      }

      if (!getCsrfToken()) {
        await refresh(true);
        return;
      }

      bootstrapRefreshInFlightRef.current = true;
      try {
        const refreshedSession = await refreshSessionRequest();
        if (isCancelled) {
          return;
        }

        setInMemoryAccessToken(refreshedSession.accessToken);
        const currentUser = await getCurrentUser();
        if (isCancelled) {
          return;
        }

        setAuthenticated(currentUser);
      } catch {
        if (!isCancelled) {
          clearSession();
        }
      } finally {
        bootstrapRefreshInFlightRef.current = false;
      }
    };

    void bootstrapAuth();

    return () => {
      isCancelled = true;
      bootstrapRefreshInFlightRef.current = false;
    };
  }, [clearSession, refresh, setAuthenticated]);

  useEffect(() => {
    const unsubscribeInvalidated = onSessionInvalidated(() => {
      track('auth_session_invalidated', undefined, { feature: 'auth' });
      clearSession();
      redirectToLoginIfNeeded();
    });

    const unsubscribeRefresh = onSessionRefreshRequested(() => {
      void refresh();
    });

    return () => {
      unsubscribeInvalidated();
      unsubscribeRefresh();
    };
  }, [clearSession, redirectToLoginIfNeeded, refresh]);

  useEffect(() => {
    setSentryUser(authState.user ? { id: authState.user.id, email: authState.user.email } : null);
  }, [authState.user]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      clearError();

      try {
        const response = await loginRequest(email, password);
        setInMemoryAccessToken(response.accessToken);
        const authUser = toAuthUser(response.user);
        setAuthenticated(authUser);
      } catch (error) {
        setAuthState((previous) => ({
          status: previous.user ? 'authenticated' : 'anonymous',
          user: previous.user,
          error: toAuthErrorMessage(error, 'Falha ao autenticar com email e senha.'),
        }));
        throw error;
      }
    },
    [clearError, setAuthenticated],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string, nonce?: string): Promise<void> => {
      clearError();

      try {
        const response = await loginWithGoogleRequest(idToken, nonce);
        setInMemoryAccessToken(response.accessToken);
        const authUser = toAuthUser(response.user);
        setAuthenticated(authUser);
      } catch (error) {
        setAuthState((previous) => ({
          status: previous.user ? 'authenticated' : 'anonymous',
          user: previous.user,
          error: toAuthErrorMessage(error, 'Falha ao autenticar com Google.'),
        }));
        throw error;
      }
    },
    [clearError, setAuthenticated],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      if (!isE2EModeEnabled()) {
        await logoutRequest();
      }
    } finally {
      clearSession();
      redirectToLoginIfNeeded();
    }
  }, [clearSession, redirectToLoginIfNeeded]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: authState.status,
      user: authState.user,
      error: authState.error,
      isAuthenticated: authState.status === 'authenticated',
      isLoading: authState.status === 'loading',
      setError,
      clearError,
      login,
      loginWithGoogle,
      refresh,
      logout,
    }),
    [authState, clearError, login, loginWithGoogle, logout, refresh, setError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
};
